/**
 * RvAnalysisSetupModal — the village Suggestion Engine's "Strategy Analysis Setup" (owner, 24 Sep
 * 2026: "the suggestion engine for this module deserves to have a 'Strategy analysis set-up' …
 * use logic — which state would you buy in?").
 *
 * The engine builds strategies that SPEND the sale proceeds — on an investment property, on a
 * lifetime annuity, into shares — and three of its assumptions are the client's to make, not the
 * tool's. Each question is asked only when a strategy would actually turn on it:
 *   - the state a bought investment property would be in (its stamp duty scale) — only where the
 *     sale frees enough to buy one; defaults to the state chosen on the village overview;
 *   - the most of the freed-up money a lifetime annuity may take — only where an annuity would be
 *     offered; defaults to a quarter, visible and changeable, so the field never leads with a plan
 *     that commits every spare dollar to an annuity;
 *     zero means "the bills are paid".
 * The two just-in-time questions the engine already asked (how new money is invested; the transfer
 * balance cap) are folded in here so the adviser answers once, on one screen, before the run.
 *
 * Mirrors CoupleValidationModal (aged care) in shape and voice. Cancel closes without changing the
 * saved answers; Continue saves them and the engine runs.
 */
import { villageInterestHeld, villageExitEntitlement, villageFeesAfterLeaving, todayIsoOf } from '../../../../shared/src/calculation/support/villageExit.ts';
import { villageMoveOutLine } from '../agedCare/villageExitWords.ts';
import type { FactFind } from '../../../../shared/src/calculation/factFindTypes.ts';
import React, { useEffect, useMemo, useState } from 'react';
import { CurrencyInput } from '../../components/ui/index.tsx';
import { formatCurrency } from '../../utils/index.ts';
import { INVESTMENT_PROFILES, INVESTMENT_PROFILE_IDS, type InvestmentProfileId } from '../../../../shared/src/calculation/investmentProfiles.ts';
import { STAMP_DUTY_STATES, isStampDutyState } from '../../../../shared/src/calculation/stampDuty.ts';
import { GENERAL_TRANSFER_BALANCE_CAP } from '../../constants/index.ts';
import { DEFAULT_RV_ANNUITY_SHARE_CAP_PCT, type RvEngineSetup, type RvAnnuityAppetite, type RvAnnuityResidual } from '../../strategy/retirementVillageRecommendedStrategies.ts';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import { guaranteeWindowWords } from '../../components/strategy/annuityWords.ts';
import { guaranteedShapeAvailable } from '../../../../shared/src/calculation/carePlusSplit.ts';
import { calculateAge } from '../../utils/index.ts';

export interface RvAnalysisSetupExtras {
    /** The new-money profile chosen here, or null when skipped (run at Conservative, ask again next time). */
    newMoneyProfile: InvestmentProfileId | null;
    /** The transfer balance account figure, or null when skipped (estimate from current balances). */
    tbcAmount: number | null;
}

export interface RvAnalysisSetupModalProps {
    isOpen: boolean;
    onClose: () => void;
    factFind: DraftFactFind;
    /** The answers as they stand (saved on the client record, or the defaults). */
    initial: RvEngineSetup;
    /** What the home sale frees after selling costs and the entry contribution; 0 when there is no home. */
    netFreedAfterEntry: number;
    /** Ask how new money would be invested (only when unanswered and this run would invest). */
    askNewMoney: boolean;
    /** Ask the transfer balance cap question for this person (only when the $1.6m screen trips). */
    tbcPerson: 'client' | 'partner' | null;
    onContinue: (setup: RvEngineSetup, extras: RvAnalysisSetupExtras) => void;
}

/** The candidates that buy a property or an annuity need this much freed up — the engine's own gate. */
export const RV_SETUP_SPEND_THRESHOLD = 50_000;

const clampPct = (v: unknown): number => {
    const n = Number(v);
    if (!Number.isFinite(n)) return DEFAULT_RV_ANNUITY_SHARE_CAP_PCT;
    return Math.min(100, Math.max(0, Math.round(n)));
};

/** A row of pill choices — the same look as the goal chips, so it reads as one product. */
/*
 * A GRID, NOT A WRAPPING ROW (owner, 27 Sep 2026). On flex-wrap each button was as wide as its own
 * label, so the answers to one question came out at three different sizes and the wordiest one
 * looked like the recommended one. Equal columns make them read as what they are: alternatives of
 * equal standing. The column count has to be a literal class name, because Tailwind builds from the
 * source text and an interpolated `grid-cols-${n}` is never emitted.
 */
const CHOICE_COLUMNS: Record<number, string> = {
    1: 'sm:grid-cols-1',
    2: 'sm:grid-cols-2',
    3: 'sm:grid-cols-3',
    4: 'sm:grid-cols-4',
};

function Choice<T extends string | number>({ options, value, onChange, ariaLabel }: { options: Array<{ id: T; label: string }>; value: T; onChange: (v: T) => void; ariaLabel: string }) {
    return (
        <div className={`grid grid-cols-1 ${CHOICE_COLUMNS[options.length] || 'sm:grid-cols-3'} gap-2`} role="radiogroup" aria-label={ariaLabel}>
            {options.map((o) => (
                <button
                    key={String(o.id)}
                    type="button"
                    role="radio"
                    aria-checked={value === o.id}
                    onClick={() => onChange(o.id)}
                    className={`rounded-lg border-2 px-3.5 py-2.5 text-sm font-semibold transition-all ${
                        value === o.id ? 'border-blue-600 bg-blue-50 text-blue-900 shadow-sm' : 'border-slate-200 bg-slate-50 text-slate-700 hover:border-slate-300 hover:bg-white'
                    }`}
                >
                    {o.label}
                </button>
            ))}
        </div>
    );
}

const SectionIcon = ({ path }: { path: string }) => (
    <div className="p-2 bg-blue-100 rounded-lg shrink-0">
        <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={path} />
        </svg>
    </div>
);

const RvAnalysisSetupModal = ({ isOpen, onClose, factFind, initial, netFreedAfterEntry, askNewMoney, tbcPerson, onContinue }: RvAnalysisSetupModalProps) => {
    const [purchaseState, setPurchaseState] = useState<string>(initial.purchaseState || '');
    const [annuityCapPct, setAnnuityCapPct] = useState<number>(clampPct(initial.annuityShareCapPct ?? DEFAULT_RV_ANNUITY_SHARE_CAP_PCT));
    const [annuityAppetite, setAnnuityAppetite] = useState<RvAnnuityAppetite>(initial.annuityAppetite ?? 'unsure');
    const [annuityResidual, setAnnuityResidual] = useState<RvAnnuityResidual>(initial.annuityResidual ?? 'both');
    const [cashflowTarget, setCashflowTarget] = useState<number | ''>(Number(initial.cashflowTarget) > 0 ? Number(initial.cashflowTarget) : 0);
    const [newMoneyProfile, setNewMoneyProfile] = useState<InvestmentProfileId | ''>('');
    const [tbcAmount, setTbcAmount] = useState<number | ''>('');
    /** Moving from another village: the current unit's payout at the move (default), or on the state's timeline. */
    const [oldUnitPaysAtMove, setOldUnitPaysAtMove] = useState<boolean>(initial.oldUnitPaysAtMove !== false);

    useEffect(() => {
        if (!isOpen) return;
        setPurchaseState(initial.purchaseState || '');
        setAnnuityCapPct(clampPct(initial.annuityShareCapPct ?? DEFAULT_RV_ANNUITY_SHARE_CAP_PCT));
        setAnnuityAppetite(initial.annuityAppetite ?? 'unsure');
        setAnnuityResidual(initial.annuityResidual ?? 'both');
        setCashflowTarget(Number(initial.cashflowTarget) > 0 ? Number(initial.cashflowTarget) : 0);
        setNewMoneyProfile('');
        setTbcAmount('');
    }, [isOpen, initial.purchaseState, initial.annuityShareCapPct, initial.cashflowTarget, initial.annuityAppetite, initial.annuityResidual]);

    // Hooks before the early return: a pop-over that returns null while closed must call the same
    // hooks every render, or React throws when it opens (owner-caught, 8 Oct 2026).
    const currentVillage = useMemo(() => {
        const ff = factFind as unknown as FactFind;
        if (!villageInterestHeld(ff)) return null;
        const today = todayIsoOf(new Date());
        const e = villageExitEntitlement(ff, today);
        return e ? { name: ff.client?.firstName || 'The client', ...e, feesAfterLeaving: villageFeesAfterLeaving(ff.retirementVillage, today, e.refundDate) } : null;
    }, [factFind]);
    if (!isOpen) return null;

    const spends = netFreedAfterEntry >= RV_SETUP_SPEND_THRESHOLD;
    /*
     * The guarantee window in this client's own years, for the residual question. Worked out once
     * here rather than inside the JSX, so the sentence and the buttons cannot drift apart. It states
     * the window, never a life expectancy — the line the owner drew on 23 September.
     */
    // The guaranteed shape is sold only into government-subsidised care; a village client
    // qualifies only where a care need is recorded.
    const offerGuaranteed = guaranteedShapeAvailable(factFind as never);
    const guaranteeWindow = guaranteeWindowWords({
        age: factFind?.client?.dob ? calculateAge(factFind.client.dob) : null,
        sex: factFind?.client?.sex,
        partnerAge: factFind?.adviceFor === 'couple' && factFind?.partner?.dob ? calculateAge(factFind.partner.dob) : null,
        partnerSex: factFind?.partner?.sex,
        ownerName: factFind?.client?.firstName,
        partnerName: factFind?.partner?.firstName,
    });
    const annuityDollars = Math.round(netFreedAfterEntry * (annuityCapPct / 100));
    const chosenState = isStampDutyState(purchaseState) ? STAMP_DUTY_STATES.find((s) => s.code === purchaseState) : undefined;
    const tbcName = (tbcPerson === 'partner' ? factFind?.partner?.firstName : factFind?.client?.firstName) || 'this person';

    const handleContinue = () => {
        onContinue(
            {
                purchaseState: chosenState ? chosenState.code : '',
                annuityShareCapPct: annuityCapPct,
                annuityAppetite,
                annuityResidual,
                cashflowTarget: cashflowTarget === '' ? 0 : Math.max(0, Number(cashflowTarget) || 0),
                oldUnitPaysAtMove,
            },
            {
                newMoneyProfile: newMoneyProfile === '' ? null : newMoneyProfile,
                tbcAmount: tbcAmount === '' ? null : Number(tbcAmount),
            },
        );
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="rv-setup-title">
            <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[85vh] overflow-hidden">
                <div className="bg-gradient-to-r from-blue-500 to-teal-400 text-white p-6 relative overflow-hidden">
                    <div className="absolute inset-0 bg-black opacity-10"></div>
                    <div className="relative z-10 flex justify-between items-start">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-white bg-opacity-20 rounded-xl backdrop-blur-sm">
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                            <div>
                                <h2 id="rv-setup-title" className="text-2xl font-bold mb-1">Strategy Analysis Setup</h2>
                                <p className="text-blue-100">A few choices the strategies turn on — yours to make, not the tool&apos;s</p>
                            </div>
                        </div>
                        <button type="button" onClick={onClose} aria-label="Close" className="text-white hover:text-blue-200 text-2xl leading-none transition-colors p-1 hover:bg-white hover:bg-opacity-10 rounded-full">
                            &times;
                        </button>
                    </div>
                </div>

                <div className="p-6 overflow-y-auto max-h-[calc(85vh-180px)]">
                    <div className="space-y-5">
                        {/* Moving from another village (8 Oct 2026): the current unit's payout, stated first. */}
                        {currentVillage && (
                            <div className="bg-white border border-blue-200 rounded-xl p-5" data-testid="rv-from-village">
                                <h3 className="text-lg font-semibold text-blue-800 mb-1">Your current village</h3>
                                <p className="text-sm text-slate-700 mb-3"><strong>When will the current unit be paid out?</strong></p>
                                <Choice<'move' | 'limit'>
                                    ariaLabel="When the current unit pays out"
                                    value={oldUnitPaysAtMove ? 'move' : 'limit'}
                                    onChange={(v) => setOldUnitPaysAtMove(v === 'move')}
                                    options={[
                                        { id: 'move', label: 'At the move' },
                                        { id: 'limit', label: `Later — up to ${currentVillage.refundMonths} months after leaving` },
                                    ]}
                                />
                                <p className="text-sm text-slate-700 mt-3">{villageMoveOutLine(currentVillage, 'village', { atMove: oldUnitPaysAtMove })}</p>
                                <p className="text-xs text-slate-500 mt-2">The terms come from the Client Profile (Living Arrangements). Change them there if they are not right.</p>
                            </div>
                        )}
                        {/* The "yearly surplus the plan must keep" question lived here until 2 Oct 2026. The
                            owner removed it: the client has already given their expenses, so "cashflow
                            covered" is judged against those, and the strongest cashflow rises on its own
                            card. The target stays at zero — "the bills are paid". */}
                        {/* 2. A bought investment property — only where the sale frees enough to buy one */}
                        {spends && (
                            <div className="bg-white border border-blue-200 rounded-xl p-5">
                                <div className="flex items-start gap-3">
                                    <SectionIcon path="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                                    <div className="flex-1">
                                        <h3 className="text-lg font-semibold text-blue-800 mb-1">If a strategy buys an investment property</h3>
                                        <p className="text-sm text-slate-700 mb-3">
                                            Selling the home frees about <strong>{formatCurrency(netFreedAfterEntry)}</strong> after the entry contribution, so some strategies buy an investment property with it.
                                            Stamp duty depends on where. <strong>Which state would the property be in?</strong>
                                        </p>
                                        <div className="max-w-xs">
                                            <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="rv-purchase-state">State of the investment property</label>
                                            <select
                                                id="rv-purchase-state"
                                                value={chosenState ? chosenState.code : ''}
                                                onChange={(e) => setPurchaseState(e.target.value)}
                                                className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            >
                                                <option value="">Not sure — leave stamp duty out</option>
                                                {STAMP_DUTY_STATES.map((s) => (
                                                    <option key={s.code} value={s.code}>{s.name}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <p className="text-xs text-gray-500 mt-2">
                                            {chosenState
                                                ? `The duty is struck on ${chosenState.name}'s standard scale with no concessions, comes out of the sale proceeds, and the property is sized to what is left. Each card says how much.`
                                                : 'With no state chosen no duty is charged, and every card that buys a property says so. No state, no guess.'}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* 3. Guaranteed income for life — only where an annuity would be offered. Asked in
                            everyday words (owner, 24 Sep 2026): a client should not need to know what an
                            annuity is to answer. The engine maps the answers to which annuity candidates it
                            builds, and how large. */}
                        {spends && (
                            <div className="bg-white border border-blue-200 rounded-xl p-5">
                                <div className="flex items-start gap-3">
                                    <SectionIcon path="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    <div className="flex-1">
                                        <h3 className="text-lg font-semibold text-blue-800 mb-1">Guaranteed income for life</h3>
                                        <p className="text-sm text-slate-700 mb-3">
                                            Some strategies lock part of the freed-up money away with an insurer in exchange for a <strong>guaranteed income for life</strong> (a lifetime annuity).
                                            Centrelink counts only 60% of that money, which can lift the Age Pension. Setting one up takes more than a form:{' '}
                                            you get a quote from the insurer, directly or through an adviser, then apply.{' '}
                                            <strong>Should we include these options?</strong>
                                        </p>
                                        <Choice<RvAnnuityAppetite>
                                            ariaLabel="Should we include a lifetime annuity"
                                            value={annuityAppetite}
                                            onChange={setAnnuityAppetite}
                                            options={[
                                                { id: 'none', label: 'No — keep it all accessible' },
                                                { id: 'yes', label: 'Yes — include these options' },
                                            ]}
                                        />
                                        {annuityAppetite === 'yes' && (
                                            <>
                                                <p className="mt-4 mb-2 text-sm text-slate-700">
                                                    <strong>Should any of it come back to the family on death?</strong> Guaranteeing more means a lower income.
                                                    The full amount only comes back if death comes early{guaranteeWindow ? `, and ${guaranteeWindow}` : ''}; after that it falls away to nothing.
                                                </p>
                                                <Choice<RvAnnuityResidual>
                                                    ariaLabel="Should the money come back to the family"
                                                    value={annuityResidual}
                                                    onChange={setAnnuityResidual}
                                                    options={[
                                                        { id: 'returned', label: 'Yes — as much as the rules allow' },
                                                        ...(offerGuaranteed ? [{ id: 'guaranteed' as const, label: 'Yes — the full amount, whenever I die' }] : []),
                                                        { id: 'nil', label: 'No — the highest income' },
                                                        { id: 'both', label: 'Show me whichever works best' },
                                                    ]}
                                                />
                                            </>
                                        )}
                                        {annuityAppetite !== 'none' && (
                                            <>
                                                <p className="mt-4 mb-2 text-sm text-slate-700"><strong>How much of the {formatCurrency(netFreedAfterEntry)} freed up could be locked away, at most?</strong></p>
                                                <Choice<number>
                                                    ariaLabel="How much could be locked away at most"
                                                    value={annuityCapPct}
                                                    onChange={setAnnuityCapPct}
                                                    options={[
                                                        { id: 10, label: 'A small part (10%)' },
                                                        { id: 25, label: 'A quarter (25%)' },
                                                        { id: 50, label: 'Half (50%)' },
                                                        { id: 100, label: 'All of it (100%)' },
                                                        ...([10, 25, 50, 100].includes(annuityCapPct) ? [] : [{ id: annuityCapPct, label: `${annuityCapPct}% (as set)` }]),
                                                    ]}
                                                />
                                                <p className="text-xs text-gray-500 mt-2">
                                                    = <span className="font-semibold tabular-nums">{formatCurrency(annuityDollars)}</span>. The rest stays invested and accessible.{' '}
                                                    {annuityDollars < 25_000
                                                        ? 'Under $25,000 is too small to be worth modelling, so no such strategy will be offered at this setting.'
                                                        : annuityAppetite === 'unsure' || annuityResidual === 'both'
                                                            ? 'Both versions are modelled: as much coming back as the rules allow (a lower income), and nothing coming back (a higher income).'
                                                            : annuityResidual === 'returned'
                                                                ? 'Only the version where the money comes back is modelled.'
                                                                : 'Only the higher-income version, where nothing comes back, is modelled.'}
                                                </p>
                                            </>
                                        )}
                                        {annuityAppetite === 'none' && (
                                            <p className="text-xs text-gray-500 mt-2">No strategy that locks money away for life will be offered.</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* 4. How new money is invested — the question every engine asks once */}
                        {askNewMoney && (
                            <div className="bg-white border border-blue-200 rounded-xl p-5">
                                <div className="flex items-start gap-3">
                                    <SectionIcon path="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                                    <div className="flex-1">
                                        <h3 className="text-lg font-semibold text-blue-800 mb-1">Investing freed-up money</h3>
                                        <p className="text-sm text-slate-700 mb-3">
                                            Some strategies invest what the sale frees. <strong>How would that money be invested?</strong> Leave it and the tool assumes Conservative, the most cautious option, and asks again next time.
                                        </p>
                                        <select
                                            aria-label="How new money would be invested"
                                            value={newMoneyProfile}
                                            onChange={(e) => setNewMoneyProfile(e.target.value as InvestmentProfileId | '')}
                                            className="w-full max-w-md px-4 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        >
                                            <option value="">Not sure — assume Conservative (most cautious)</option>
                                            {INVESTMENT_PROFILE_IDS.map((id) => (
                                                <option key={id} value={id}>{INVESTMENT_PROFILES[id].label}</option>
                                            ))}
                                        </select>
                                        <p className="text-xs text-gray-500 mt-2">Saved to the client profile, so you&apos;ll only be asked once.</p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* 5. The transfer balance cap — only when the $1.6m screen trips */}
                        {tbcPerson && (
                            <div className="bg-white border border-blue-200 rounded-xl p-5">
                                <div className="flex items-start gap-3">
                                    <SectionIcon path="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                    <div className="flex-1">
                                        <h3 className="text-lg font-semibold text-blue-800 mb-1">{tbcName}&apos;s pension cap</h3>
                                        <p className="text-sm text-slate-700 mb-3">
                                            The government limits how much any one person may move into a retirement pension over their lifetime — currently{' '}
                                            <strong>{formatCurrency(GENERAL_TRANSFER_BALANCE_CAP)}</strong> — and {tbcName}&apos;s balances are large enough that it may now cap how much more can go in.
                                            Roughly how much has {tbcName} moved into retirement-phase pensions so far? The ATO shows this in myGov as the <strong>transfer balance account</strong>.
                                            Leave it blank and the tool estimates from current pension balances, and the advice document says so.
                                        </p>
                                        <div className="max-w-xs">
                                            <CurrencyInput label="Lifetime amount moved into pensions ($)" name="rvTbcAmount" value={tbcAmount} onChange={(v) => setTbcAmount(v ?? '')} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <div className="flex justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
                    <button type="button" onClick={onClose} className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-all duration-200 font-semibold">
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleContinue}
                        className="px-6 py-2 bg-gradient-to-r from-blue-500 to-teal-400 text-white rounded-lg hover:from-blue-600 hover:to-teal-500 transition-all duration-200 font-semibold shadow-md"
                    >
                        Continue to Analysis
                    </button>
                </div>
            </div>
        </div>
    );
};

export default RvAnalysisSetupModal;
